import { getPlatformBrandIcon } from '@/components/icons/platform-brand-icon';
import {
  getPlatformDotColorClass,
  getPlatformHexColorValue,
  getPlatformLabel as getPlatformIdentityLabel,
  sortPlatformKeys,
} from '@/lib/platforms/identity';
import type { PlatformType } from '@/services/socialAuth/types';

// Get platform display label
export const getPlatformLabel = (platform: PlatformType | string): string => {
  return getPlatformIdentityLabel(platform);
};

// Get platform icon component
export const getPlatformIcon = (
  platform: PlatformType | string,
): ReturnType<typeof getPlatformBrandIcon> => getPlatformBrandIcon(platform);

// First-wins per-platform lookup. This is intentionally lossy — it exists
// for call sites that only need "is this platform connected, and if so what
// is ONE representative account" (e.g. a platform-level boolean, or the
// detail-view keyed by platform). It must NOT be used to render a list of
// connections, because since 2026-07-13 the DB stops retiring prior
// connections for instagram/facebook/linkedin/tiktok/pinterest — a platform
// can legitimately have 2+ active, billable connections, and this helper
// would silently discard every one after the first.
export const groupAccountsByPlatform = <T extends { platform: string }>(
  accounts: T[],
): Record<string, T> => {
  return accounts.reduce(
    (groups, account) => {
      const platform = account.platform;
      if (!groups[platform]) {
        groups[platform] = account;
      }
      return groups;
    },
    {} as Record<string, T>,
  );
};

// Every connection, grouped by platform, preserving order. Use this for
// anything that renders a list of connected accounts — a platform with
// multiple active connections must show every one of them, not just the
// first (see comment on `groupAccountsByPlatform` above).
export const groupAccountConnectionsByPlatform = <T extends { platform: string }>(
  accounts: T[],
): Record<string, T[]> => {
  return accounts.reduce(
    (groups, account) => {
      const platform = account.platform;
      (groups[platform] ??= []).push(account);
      return groups;
    },
    {} as Record<string, T[]>,
  );
};

export const getPlatformDotColor = (platform: string): string => {
  return getPlatformDotColorClass(platform);
};

export const getPlatformHexColor = (platform: string): string => {
  return getPlatformHexColorValue(platform);
};

// Sort platforms by a specific order
export const sortPlatforms = (platforms: string[]): string[] => {
  return sortPlatformKeys(platforms);
};
